// Client-Side Data Sources Configuration
// All data is fetched from GitHub or local fallback JSONs
// NO backend server required

const GITHUB_CONFIG = {
  token: process.env.REACT_APP_GITHUB_TOKEN,
  owner: process.env.REACT_APP_REPO_OWNER || 'Nibyor2025',
  repo: process.env.REACT_APP_REPO_NAME || 'newsletter'
};

// GitHub raw content URLs for data files
const GITHUB_RAW_BASE = `https://raw.githubusercontent.com/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/main`;

// Data source URLs - GitHub first, local fallback
export const DATA_SOURCES = {
  // Job opportunities data
  jobs: {
    primary: `${GITHUB_RAW_BASE}/data/jobs.json`,
    fallback: '/data/jobs-fallback.json',
    local: '/data/jobs-fallback.json'
  },
  
  // News articles / RSS feeds
  news: {
    primary: `${GITHUB_RAW_BASE}/data/news.json`,
    fallback: '/data/news-fallback.json',
    local: '/data/news-fallback.json'
  },
  
  // ADA scan history
  adaScans: {
    primary: `${GITHUB_RAW_BASE}/ada-scans`,
    folder: 'ada-scans'
  },
  
  // Analytics data (optional, for admin dashboard)
  analytics: {
    primary: `${GITHUB_RAW_BASE}/data/analytics.json`,
    fallback: '/data/analytics.json',
    local: '/data/analytics.json'
  }
};

// GitHub API configuration (for writing data back)
export const GITHUB_API = {
  baseUrl: 'https://api.github.com',
  reposUrl: `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}`,
  headers: {
    'Authorization': `Bearer ${GITHUB_CONFIG.token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/vnd.github.v3+json'
  }
};

/**
 * Fetch data from primary source (GitHub) with fallback to local
 * @param {string} sourceKey - Key from DATA_SOURCES object
 * @param {object} options - Fetch options
 * @returns {Promise<any>} - Parsed JSON data
 */
export async function fetchDataWithFallback(sourceKey, options = {}) {
  const source = DATA_SOURCES[sourceKey];
  if (!source) {
    console.error(`Unknown data source: ${sourceKey}`);
    return null;
  }

  const timeout = options.timeout || 10000;

  // Try primary source (GitHub)
  try {
    console.log(`📡 Fetching ${sourceKey} from GitHub...`);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Add cache-busting query param to bypass CDN caching after repository updates
    const primaryUrl = source.primary + (source.primary.includes('?') ? '&' : '?') + `_=${Date.now()}`;

    const response = await fetch(primaryUrl, {
      ...options,
      signal: controller.signal,
      headers: {
        'Cache-Control': 'no-cache',
        ...options.headers
      }
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      console.log(`✅ Loaded ${sourceKey} from GitHub`);
      return data;
    }
  } catch (error) {
    console.warn(`⚠️ GitHub ${sourceKey} unavailable:`, error.message);
  }

  // Fallback to local JSON
  try {
    console.log(`📦 Loading ${sourceKey} from local fallback...`);
    const response = await fetch(source.fallback || source.local);
    if (response.ok) {
      const data = await response.json();
      console.log(`✅ Loaded ${sourceKey} from local fallback`);
      return data;
    }
  } catch (error) {
    console.error(`❌ Failed to load ${sourceKey} fallback:`, error.message);
  }

  return null;
}

/**
 * Save data to GitHub repository
 * @param {string} path - File path in repository (e.g., 'data/jobs.json')
 * @param {object} data - Data to save
 * @param {string} message - Commit message
 * @returns {Promise<boolean>} - Success status
 */
export async function saveDataToGitHub(path, data, message = 'Update data') {
  if (!GITHUB_CONFIG.token) {
    console.error('❌ GitHub token not configured');
    return false;
  }

  try {
    const url = `${GITHUB_API.reposUrl}/contents/${path}`;

    // Get current file SHA (required for updates)
    let sha;
    try {
      const currentFile = await fetch(url, {
        headers: GITHUB_API.headers
      });
      if (currentFile.ok) {
        const fileData = await currentFile.json();
        sha = fileData.sha;
      }
    } catch (error) {
      // File doesn't exist yet, that's okay
      console.log('Creating new file...');
    }

    // Encode data as base64
    const content = btoa(unescape(encodeURIComponent(JSON.stringify(data, null, 2))));

    // Create or update file
    const response = await fetch(url, {
      method: 'PUT',
      headers: GITHUB_API.headers,
      body: JSON.stringify({
        message,
        content,
        sha // Include SHA if updating existing file
      })
    });

    if (response.ok) {
      console.log(`✅ Saved ${path} to GitHub`);
      return true;
    } else {
      const error = await response.json();
      console.error('❌ GitHub save failed:', error.message);
      return false;
    }
  } catch (error) {
    console.error('❌ Error saving to GitHub:', error.message);
    return false;
  }
}

/**
 * Check if GitHub integration is configured
 * @returns {boolean}
 */
export function isGitHubConfigured() {
  return !!(GITHUB_CONFIG.token && GITHUB_CONFIG.owner && GITHUB_CONFIG.repo);
}

/**
 * Get GitHub repository URL
 * @returns {string}
 */
export function getRepoUrl() {
  return `https://github.com/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}`;
}

export default {
  DATA_SOURCES,
  GITHUB_API,
  GITHUB_CONFIG,
  fetchDataWithFallback,
  saveDataToGitHub,
  isGitHubConfigured,
  getRepoUrl
};
